import { useMemo } from 'react';
import { User } from '../../../graphql/graphql';
import { UserEditedMeta } from '../../../Components/PatientProfile/PatientProfileParts/PatientProfileInformation';

export interface UserVerification {
	email: boolean;
	phoneNumber: boolean;
	birthDate: boolean;
	birthNumber: boolean;
	address: boolean;
}

type VerificationAttributeName =
	| 'EMAIL'
	| 'PHONE'
	| 'BIRTH_DATE'
	| 'BIRTH_NUMBER'
	| 'ADDRESS';

const defaultVerification: UserVerification = {
	email: false,
	phoneNumber: false,
	birthDate: false,
	birthNumber: false,
	address: false,
};

const normalizeValue = (value?: string | null) => value?.trim() || null;

const isVerificationAttributeName = (
	value?: string | null,
): value is VerificationAttributeName =>
	['EMAIL', 'PHONE', 'BIRTH_DATE', 'BIRTH_NUMBER', 'ADDRESS'].includes(
		value ?? '',
	);

const getBirthNumber = (profile?: User['profile'] | null) =>
	normalizeValue(profile?.formattedBirthNumber) ??
	normalizeValue(profile?.birthNumber);

const getVerificationItem = (
	user: User | UserEditedMeta,
	attributeName: VerificationAttributeName,
	currentValue?: string | null,
	originalValue?: string | null,
): boolean => {
	const userVerification = user.originalUser?.userVerifications?.find(
		verification =>
			isVerificationAttributeName(verification.attributeName) &&
			verification.attributeName === attributeName,
	);

	//IF VIRTUAL AND ORIGINAL HAVE DIFFERENT VALUE
	if (normalizeValue(currentValue) !== normalizeValue(originalValue)) {
		return false;
	}

	return Boolean(userVerification?.isVerified);
};

export function usePatientVerification(user: User | UserEditedMeta | null) {
	const verification: UserVerification = useMemo(() => {
		if (!user || !('originalUser' in user)) {
			return defaultVerification;
		}

		return {
			email: getVerificationItem(
				user,
				'EMAIL',
				user.email,
				user.originalUser?.email,
			),
			phoneNumber: getVerificationItem(
				user,
				'PHONE',
				user.formattedPhoneNumber,
				user.originalUser?.formattedPhoneNumber,
			),
			birthDate: getVerificationItem(
				user,
				'BIRTH_DATE',
				user.profile?.birthDate,
				user.originalUser?.profile?.birthDate,
			),
			birthNumber: getVerificationItem(
				user,
				'BIRTH_NUMBER',
				getBirthNumber(user.profile),
				getBirthNumber(user.originalUser?.profile),
			),
			address: getVerificationItem(
				user,
				'ADDRESS',
				user.profile?.address?.fullAddress,
				user.originalUser?.profile?.address?.fullAddress,
			),
		};
	}, [user]);

	return { verification };
}
